import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowRightLeft, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const POLL_MS = 60_000;

// Persistent banner shown whenever the signed-in user is an active Dibs
// collaborator. Two modes:
//   * IN a collaborator session (the active business is one they were granted):
//     amber bar with the client's name + Leave; re-checks the grant every 60s
//     and ends the session the moment the owner revokes it (client-side half of
//     enforcement; RLS is the server-side half).
//   * NOT in one (e.g. an operator who also owns their own business and landed
//     on their own dashboard): slim bar offering to switch into a client
//     dashboard. Without this, owner-operators had no way in, and the old
//     gate (isSupportCollaborator alone) checked the WRONG business and
//     force-signed them out in a loop.
//
// When the operator holds grants for MORE THAN ONE business, both modes render
// a picker so they can jump straight between clients (e.g. helping several at
// once during the same window) without leaving and re-entering one at a time.
const CollaboratorSessionBanner = () => {
  const {
    isSupportCollaborator,
    isAdmin,
    isStaff,
    businessId,
    collaboratorBusinesses,
    switchCollaboratorBusiness,
    signOut,
  } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);

  const businesses = collaboratorBusinesses ?? [];
  const collabBiz = businesses.find((b) => b.business_id === businessId) || null;
  // Only a collaborator SESSION when the active business is a granted one.
  const isCollabSession = isSupportCollaborator && !!collabBiz;
  const businessName = collabBiz?.business_name || null;
  const hasMultiple = businesses.length > 1;

  const leave = useCallback(
    async (reason: "manual" | "expired" | "revoked") => {
      if (leavingRef.current) return;
      leavingRef.current = true;
      setLeaving(true);
      // Owner-operators keep their own account session: ending the collab
      // session just drops the selection and returns them to their own
      // dashboard. Pure operators (no role of their own) are signed out.
      if (isAdmin || isStaff) {
        localStorage.removeItem("dibs_collab_active_business");
        window.location.replace("/");
        return;
      }
      try {
        await signOut();
      } finally {
        navigate(
          reason === "manual" ? "/auth" : `/auth?collaborator_session=${reason}`,
          { replace: true }
        );
      }
    },
    [isAdmin, isStaff, signOut, navigate]
  );

  const check = useCallback(async () => {
    if (!isCollabSession || !businessId) return;
    const { data, error } = await (supabase as any)
      .from("support_collaborators")
      .select("status, expires_at")
      .eq("business_id", businessId)
      .eq("status", "active")
      .order("expires_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    // Transient errors shouldn't sign the operator out; only a definitive read.
    if (error) return;
    if (!data) {
      await leave("revoked");
      return;
    }
    // expires_at NULL = indefinite; only leave if an explicit expiry has passed.
    const expired = !!data.expires_at && new Date(data.expires_at).getTime() <= Date.now();
    if (expired) await leave("expired");
  }, [isCollabSession, businessId, leave]);

  useEffect(() => {
    if (!isCollabSession) return;
    check();
    const t = window.setInterval(check, POLL_MS);
    return () => window.clearInterval(t);
  }, [isCollabSession, check]);

  if (!isSupportCollaborator) return null;

  // Shared dropdown that lists every client the operator can access. The
  // currently-active client (if any) is marked and disabled.
  const clientPicker = (label: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-950/10 hover:bg-amber-950/20 px-3 py-1 transition-colors">
        <ArrowRightLeft className="h-3.5 w-3.5" />
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>Switch to a client</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {businesses.map((b) => {
          const isCurrent = b.business_id === businessId;
          return (
            <DropdownMenuItem
              key={b.business_id}
              disabled={isCurrent}
              onSelect={() => {
                if (!isCurrent) switchCollaboratorBusiness(b.business_id);
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate">{b.business_name || "Unnamed business"}</span>
              {isCurrent && <Check className="h-4 w-4 shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Collaborator with access but currently on their own dashboard. Offer the
  // way IN to the client business(es).
  if (!isCollabSession) {
    const first = businesses[0];
    if (!first) return null;
    return (
      <div className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-100 text-amber-950 px-4 py-2 text-sm font-medium shadow">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {hasMultiple ? (
              <>You have collaborator access to <strong>{businesses.length} client dashboards</strong></>
            ) : (
              <>You have collaborator access to <strong>{first.business_name}</strong></>
            )}
          </span>
        </div>
        {hasMultiple ? (
          clientPicker("Switch to a dashboard")
        ) : (
          <button
            type="button"
            onClick={() => switchCollaboratorBusiness(first.business_id)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-950/10 hover:bg-amber-950/20 px-3 py-1 transition-colors"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            Switch to their dashboard
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-500 text-amber-950 px-4 py-2 text-sm font-medium shadow">
      <div className="flex items-center gap-2 min-w-0">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span className="truncate">
          Dibs collaborator session — helping{" "}
          <strong>{businessName || "this business"}</strong>
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {hasMultiple && clientPicker("Switch client")}
        <button
          type="button"
          onClick={() => leave("manual")}
          disabled={leaving}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-950/10 hover:bg-amber-950/20 px-3 py-1 transition-colors disabled:opacity-60"
        >
          {leaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Leave
        </button>
      </div>
    </div>
  );
};

export default CollaboratorSessionBanner;
